import { ListIteratee, Many } from 'lodash';
import orderBy from 'lodash/orderBy';
import startCase from 'lodash/startCase';

import JsonSchema, {
    JsonSchemaArray, JsonSchemaBasicObject, JsonSchemaEnum,
    JsonSchemaType,
    RegularJsonSchema,
} from '@evidentid/json-schema/interfaces/JsonSchema';
import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';

import { PRODUCERS_COLUMN_LABEL } from '@/modules/entity-management/constants/producersColumn.constant';
import { isCollateralCustomProperty } from '@/utils/isCollateralCustomProperty';
import { isEquipmentCustomProperty } from '@/utils/isEquipmentCustomProperty';

const customPropertyCollateralOrder = [
    'description',
    'category',
    'uniqueIdentifier',
    'limitRequired',
    'maximumDeductible',
];

const customPropertyEquipmentOrder = [
    'description',
    'rentalId',
    'equipmentValue',
    'rentalCompleteDate',
    'rentalProtectionPlan',
];

export function getCustomPropertySchema(
    customProperties: CustomProperty[],
    customPropertiesSortingIteratees: Many<ListIteratee<CustomProperty>> = (_, index) => index,
    customPropertyOrders: Parameters<typeof orderBy>[2] = [],
): JsonSchema {
    return {
        type: JsonSchemaType.object,
        properties: {
            ...customProperties.reduce((accu, field) => {
                let property;
                if (isCollateralCustomProperty(field)) {
                    property = {
                        ...field.schema,
                        items: {
                            ...(field.schema as any).items,
                            propertiesOrder: customPropertyCollateralOrder,
                        },
                    } as JsonSchemaArray;
                } else if (isEquipmentCustomProperty(field)) {
                    property = {
                        ...field.schema,
                        items: {
                            ...(field.schema as any).items,
                            propertiesOrder: customPropertyEquipmentOrder,
                        },
                    } as JsonSchemaArray;
                } else {
                    const notSpaceOnlyRegex = '^(?!\\s+$)';
                    property = {
                        ...field.schema,
                        // FIXME: a proper way to validate trimmed string for empty check
                        ...(field.required && (field.schema as RegularJsonSchema).type === 'string' && { pattern: notSpaceOnlyRegex }),
                    };
                }
                // Equipment is excluded from the entity-details form schema, so nothing enforces a
                // minimum there. Enforcing one here would let a required property block creating an
                // entity while still allowing that same entity to be saved with no rows.
                if (
                    (field.schema as RegularJsonSchema).type === JsonSchemaType.array
                    && field.required
                    && !isEquipmentCustomProperty(field)
                ) {
                    (property as JsonSchemaArray).minItems = 1;
                }
                accu[field.key] = property;
                return accu;
            }, {} as JsonSchemaBasicObject['properties']),
        },
        propertiesOrder: orderBy(customProperties, customPropertiesSortingIteratees, customPropertyOrders)
            .map((x) => x.key),
        required: customProperties.filter((x) => x.required).map((x) => x.key),
    };
}

export function getExceptionsSchema(requirementTypes: string[]): JsonSchema {
    const requirementTypeKeyLabels = requirementTypes.map((x) => (
        { key: x, label: startCase(x.toLowerCase().replace(/_/g, ' ')) }),
    );
    return {
        type: JsonSchemaType.object,
        properties: requirementTypeKeyLabels.reduce((accu, requirementType) => ({
            ...accu, [requirementType.key]: {
                type: JsonSchemaType.string,
                format: 'date',
                title: `Existing ${requirementType.label} Policy Expiration Date`,
            },
        }), {} as JsonSchemaBasicObject['properties']),
        propertiesOrder: [ ...requirementTypes ],
        required: [],
    };
}

const producerPropertiesOrder = [ 'producerFirm', 'contactName', 'email', 'phone', 'coverageTypes', 'isDefault' ];

/** The coverage types an imported producer may be given. A type without a loaded label is shown by its key. */
export function getProducerCoverageTypeSchema(
    coverageTypes: string[], coverageTypeLabels: Record<string, string> = {},
): JsonSchemaEnum {
    return {
        type: JsonSchemaType.string,
        enum: coverageTypes,
        enumLabels: coverageTypes.map((coverageType) => coverageTypeLabels[coverageType] || coverageType),
    } as JsonSchemaEnum;
}

export function getProducerListSchema(
    coverageTypes: string[], coverageTypeLabels: Record<string, string> = {},
): JsonSchema {
    const notSpaceOnlyRegex = '^(?!\\s+$)';
    return {
        type: JsonSchemaType.array,
        title: PRODUCERS_COLUMN_LABEL,
        items: {
            type: JsonSchemaType.object,
            properties: {
                producerFirm: { type: JsonSchemaType.string, title: 'Producer Firm', minLength: 1, pattern: notSpaceOnlyRegex },
                contactName: { type: JsonSchemaType.string, title: 'Contact Name', minLength: 1, pattern: notSpaceOnlyRegex },
                email: { type: JsonSchemaType.string, title: 'Email', format: 'email', minLength: 1 },
                // The same format as the primary contact phone: validated, and sent as +1 and digits.
                phone: { type: JsonSchemaType.string, title: 'Phone', format: 'phone', minLength: 1 },
                coverageTypes: {
                    type: JsonSchemaType.array,
                    title: 'Coverage Types',
                    // An empty enum would reject every value, so an account without coverage types
                    // leaves the keys to the API to check.
                    items: (coverageTypes.length > 0
                        ? getProducerCoverageTypeSchema(coverageTypes, coverageTypeLabels)
                        : { type: JsonSchemaType.string }) as JsonSchema,
                },
                isDefault: { type: JsonSchemaType.boolean, title: 'Default' },
            },
            required: [ 'producerFirm', 'contactName', 'email', 'phone' ],
            propertiesOrder: producerPropertiesOrder,
        } as JsonSchema,
    } as JsonSchema;
}
